import { createApiClient, getApiErrorInfo } from './api';
import { config } from '../config';
import type { AxiosInstance } from 'axios';
import type { Registration } from './registrationService';

export interface DoceboDescriptionSection {
  type: string;
  content: string;
}

/**
 * On-demand course from the Docebo LMS, as returned by our backend proxy.
 */
export interface DoceboCourse {
  idCourse: number;
  name: string;
  description?: string;
  code?: string;
  type?: string;
  credits?: number;
  dateBegin?: string;
  dateEnd?: string;
  slugName?: string;
  language?: string;
  category?: string;
  developer?: string;
  level?: string;
  speakers?: { firstName: string; lastName: string; email?: string }[];
  descriptionSections?: DoceboDescriptionSection[];
}

export interface DoceboEnrollmentResult {
  courseUrl: string;
  registration?: Registration;
}

/**
 * Service for fetching Docebo on-demand courses via our backend proxy.
 */
class DoceboService {
  private api: AxiosInstance;

  constructor() {
    this.api = createApiClient(config.api.courses);
  }

  /**
   * Get all published on-demand courses from Docebo.
   * @param searchText Optional search term
   */
  async getCourses(searchText?: string): Promise<DoceboCourse[]> {
    try {
      const params = searchText ? { searchText } : undefined;
      const response = await this.api.get('/api/docebo/courses', { params });
      return response.data || [];
    } catch (error: unknown) {
      console.error('Failed to fetch Docebo courses:', error);
      const apiError = getApiErrorInfo(error);
      console.error('Docebo error details:', apiError);
      return [];
    }
  }

  /**
   * Get full detail for a single Docebo course, including description sections.
   * @param idCourse The Docebo course ID (not the frontend offset ID)
   */
  async getCourseDetail(idCourse: number): Promise<DoceboCourse | null> {
    try {
      const response = await this.api.get<DoceboCourse>(`/api/docebo/courses/${idCourse}`);
      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Enroll the current user in a Docebo course and return the URL to launch it.
   * @param courseId The internal course ID (backend resolves Docebo ID from this)
   */
  async enroll(courseId: number): Promise<DoceboEnrollmentResult> {
    const response = await this.api.post<DoceboEnrollmentResult>(
      `/api/registrations/${courseId}/docebo`
    );
    return response.data;
  }
}

export const doceboService = new DoceboService();
export default doceboService;
